/**
 * Utility functions for file handling and type detection
 */

/**
 * Supported image MIME types for preview
 */
export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg', 
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/svg+xml'
] as const;

/**
 * Check if a file is a supported image type
 */
export function isImageFile(mimeType: string): boolean {
  return (SUPPORTED_IMAGE_TYPES as readonly string[]).includes(mimeType);
}

/**
 * Check if a file is a supported image type based on file extension
 */
export function isImageFileByExtension(filename: string): boolean {
  const extension = filename.toLowerCase().split('.').pop();
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
  return extension ? imageExtensions.includes(extension) : false;
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  return filename.toLowerCase().split('.').pop() || '';
}

/**
 * Check if a document should be previewed as an image
 */
export function shouldPreviewAsImage(document: { mimeType?: string; filename: string }): boolean {
  // First check MIME type if available
  if (document.mimeType) {
    return isImageFile(document.mimeType);
  }
  
  // Fallback to file extension
  return isImageFileByExtension(document.filename);
}

/**
 * Format file size in human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
